import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useContext, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { appBarHeight } from 'src/constants/layout';
import { fiveZeroZeroErrorMessage } from 'src/constants/messages';
import LicenseContext from 'src/contexts/LicenseContext';

// API (Api::V5::DispatchTableImportsController) の上限と揃える
const MAX_FILES_COUNT = 10;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const DispatchTableImportPresenter: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      enqueueSnackbar('サポートされていないファイル形式が含まれています。.xlsx ファイルのみアップロード可能です。', { variant: 'error' });
      return;
    }
    if (files.length + acceptedFiles.length > MAX_FILES_COUNT) {
      enqueueSnackbar(`ファイル数は${MAX_FILES_COUNT}件以内にしてください。`, { variant: 'error' });
      return;
    }
    if (acceptedFiles.some((file) => file.size > MAX_FILE_SIZE)) {
      enqueueSnackbar(`ファイルサイズは1ファイルあたり${MAX_FILE_SIZE_MB}MB以内にしてください。`, { variant: 'error' });
      return;
    }
    // 同名ファイルは API 側でも 400 になる（GCS キー衝突防止）ため事前に弾く
    const mergedNames = [...files, ...acceptedFiles].map((file) => file.name);
    if (new Set(mergedNames).size !== mergedNames.length) {
      enqueueSnackbar('同名のファイルが含まれています。ファイル名を変更してアップロードしてください。', { variant: 'error' });
      return;
    }
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, [files, enqueueSnackbar]);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    noClick: true,
    noKeyboard: true,
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (files.length === 0) return;
    setIsLoading(true);

    const data = new FormData();
    files.forEach((file) => {
      data.append('files[]', file);
    });

    axios.post('/api/v5/dispatch_table_imports', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then(() => {
        enqueueSnackbar('取込を開始しました。完了時に画面とメールで通知します。');
        licenseContext.updateAsyncJobRequested(true);
        setFiles([]);
      })
      .catch((e: AxiosError<{ message: string }>) => {
        // onClick から fire-and-forget で呼ばれるため re-throw すると
        // Unhandled Promise Rejection になる。snackbar 通知で完結させる
        enqueueSnackbar(e.response?.data?.message || fiveZeroZeroErrorMessage, { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const mainContent = () => {
    if (!licenseContext.config?.use_dispatch_table_import) {
      return (
        <Typography sx={{ py: 2 }}>配車表取込機能が有効になっていません。</Typography>
      );
    }

    return (
      <Stack
        spacing={1}
        sx={{
          py: 2,
          width: 600,
        }}
      >
        <Box
          {...getRootProps()}
          sx={{
            width: '100%',
            my: 4,
            color: 'rgba(110, 119, 129, 1)',
            border: '5px dashed #E2E4E6',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 6,
          }}
        >
          <input {...getInputProps()} />
          <div>
            ドラッグ＆ドロップするか、
            <Button
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
              sx={{
                textDecoration: 'underline',
                textTransform: 'none',
                minWidth: 'auto',
                padding: 0,
                verticalAlign: 'baseline',
              }}
            >
              ファイルを選択して
            </Button>
            アップロードします。
          </div>
          <div>ファイルタイプとしては .xlsx がサポートされます。</div>
          <div>{`複数選択できます（最大${MAX_FILES_COUNT}件、1ファイル${MAX_FILE_SIZE_MB}MBまで）。`}</div>
        </Box>
        {files.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
            {files.map((file, index) => (
              <Chip
                key={`${file.name}-${index}`}
                label={file.name}
                onDelete={() => {
                  removeFile(index);
                }}
              />
            ))}
          </Stack>
        )}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            disabled={files.length === 0 || isLoading}
            onClick={handleUpload}
          >
            取込を開始
          </Button>
        </Box>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        height: `calc(100vh - ${appBarHeight}px)`,
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        overflow: 'hidden',
      }}
    >
      <Box display="flex" alignItems="center" gap={2} sx={{ height: 70, width: 600, mx: 'auto' }}>
        <Typography variant="h3" fontWeight="bold">
          配車表取込
        </Typography>
      </Box>
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          overflow: 'auto',
        }}
      >
        {mainContent()}
      </Paper>
    </Box>
  );
});

export default DispatchTableImportPresenter;
